import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getOrderedBreadcrumbItems, getBreadcrumbItems } from '../../utils/breadcrumbRoutes';
import { useBreadcrumb } from '../../context/BreadcrumbContext';
import { fetchData } from '../../api';
import { buildBreadcrumbListJsonLd } from '../../utils/breadcrumbJsonLd';
import './Breadcrumbs.css';

const ORDERED_HOME_PATHS = ['/', '/apts-home', '/villas-home', '/plots-home'];

function getCategoryTypeFromPath(pathname) {
  const p = (pathname || '').replace(/^\//, '').toLowerCase();
  const segment = p.split('/')[0] || '';
  if (segment.startsWith('rents-')) return segment.includes('villa') ? 'villa_rent' : 'apartment_rent';
  if (segment.startsWith('villas-') || segment === 'villas-home') return 'villa_buy';
  if (segment.startsWith('plots-') || segment === 'plots-home') return 'plot_buy';
  return 'apartment_buy';
}

function linkToProp(path) {
  if (typeof path !== 'string') return path;
  const [pathnamePart, searchPart] = path.split('?');
  if (searchPart) return { pathname: pathnamePart || '/', search: `?${searchPart}` };
  return path;
}

const EDITORIAL_CONTENT_RAIL =
  /^\/(apts|villas|plots)-(property-news-page|hot-localities-page)(\/|$)/;

export default function Breadcrumbs() {
  const { pathname, search } = useLocation();
  const params = useParams();
  const { overrideLabel, overrideCity, overrideLocation } = useBreadcrumb();

  const normalizedPath =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const isEditorialContentRail = EDITORIAL_CONTENT_RAIL.test(normalizedPath);

  const searchParams = React.useMemo(() => new URLSearchParams(search), [search]);
  const locationOverrides = React.useMemo(
    () => (overrideCity || overrideLocation ? { city: overrideCity, location: overrideLocation } : null),
    [overrideCity, overrideLocation]
  );

  const categorynameFromUrl = searchParams.get('categoryname') || searchParams.get('category') || '';
  const isNumericCategoryId = /^\d+$/.test(String(categorynameFromUrl).trim());
  const [resolvedCategoryLabel, setResolvedCategoryLabel] = useState(null);

  useEffect(() => {
    if (!isNumericCategoryId || !categorynameFromUrl) {
      setResolvedCategoryLabel(null);
      return;
    }
    let mounted = true;
    const resolve = async () => {
      try {
        const res = await fetchData('property-categories');
        if (!mounted || !res?.success || !Array.isArray(res?.data)) return;
        const categoryType = getCategoryTypeFromPath(pathname);
        const list = res.data.filter((i) => i.category_type === categoryType);
        const found = list.find(
          (c) =>
            String(c.id) === String(categorynameFromUrl) ||
            String(c.slug) === String(categorynameFromUrl)
        );
        if (mounted && found && (found.name || found.title)) {
          setResolvedCategoryLabel(found.name || found.title);
        } else {
          setResolvedCategoryLabel(null);
        }
      } catch {
        if (mounted) setResolvedCategoryLabel(null);
      }
    };
    resolve();
    return () => { mounted = false; };
  }, [pathname, categorynameFromUrl, isNumericCategoryId]);

  const orderedItems = getOrderedBreadcrumbItems(
    pathname,
    searchParams,
    params,
    overrideLabel,
    locationOverrides,
    resolvedCategoryLabel
  );

  if (ORDERED_HOME_PATHS.includes(pathname)) return null;

  const items =
    orderedItems.length > 0
      ? orderedItems
      : getBreadcrumbItems(pathname, params, overrideLabel);

  if (items.length === 0) return null;

  const jsonLd = buildBreadcrumbListJsonLd(items);

  return (
    <>
      {jsonLd ? (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
      ) : null}
      <nav
        className={`breadcrumbs-wrap page-shell-x w-full box-border${isEditorialContentRail ? ' editorial-content-rail' : ''}`}
        aria-label="Breadcrumb"
      >
        <div className="breadcrumbs-inner">
          <ol className="breadcrumbs-list">
            {items.map((item, index) => {
              const toProp = linkToProp(item.path);
              const isCurrent = item.isCurrent;
              return (
                <li key={`${item.path}-${index}`} className="breadcrumbs-item">
                  {isCurrent ? (
                    <span className="breadcrumbs-current" aria-current="page">
                      {item.label}
                    </span>
                  ) : (
                    <Link to={toProp} className="breadcrumbs-link">
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
}
